import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import { Users, UserCog, Check, X } from "lucide-react";
import StageRegistrationsPanel from "../components/StageRegistrationsPanel";
import OrdersPanel from "../components/OrdersPanel";

const ROLES = ["member", "secretary", "treasurer", "admin"];
const ROLE_BADGE = {
  admin: "bg-club-red text-white",
  treasurer: "bg-club-yellow text-navy",
  secretary: "bg-navy text-white",
  member: "bg-slate-200 text-slate-700",
};

export default function Secretariat() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/secretariat/members");
      setMembers(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  if (!user || !(["admin", "secretary"].includes(user.role) || (user.roles || []).some((r) => ["admin", "secretary"].includes(r)))) return null;

  const filtered = filter === "all" ? members : members.filter((m) => m.role === filter);

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="secretariat-page">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="label-eyebrow text-club-red mb-3">{t.secretary.title}</div>
        <h1 className="font-serif text-4xl text-navy">{tr("Secrétariat")}</h1>
        <div className="divider-gold mt-5 mb-8" />

        {/* Tabs */}
        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mb-6 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin" data-testid="secretariat-tabs">
          {[
            { k: "members",            l: tr("Membres") },
            { k: "stage-inscriptions", l: tr("Inscriptions stages") },
            { k: "orders",             l: tr("Commandes boutique") },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 whitespace-nowrap shrink-0 ${tab === k ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid={`secret-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "stage-inscriptions" && <StageRegistrationsPanel embedded />}
        {tab === "orders" && <OrdersPanel embedded />}

        {tab === "members" && (<>

        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="secretariat-filter">
          {["all", ...ROLES].map((r) => (
            <button key={r} onClick={() => setFilter(r)}
              className={`px-3 py-2 text-[11px] uppercase tracking-[0.2em] ${filter === r ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-navy"}`}
              data-testid={`secret-filter-${r}`}>
              {r === "all" ? t.common.all : r} ({r === "all" ? members.length : members.filter((m) => m.role === r).length})
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="members-table">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white text-left">
              <tr>
                {["Nom", "Email", "Rôle", "Téléphone", "Catégorie", "Inscrit le", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-slate-100" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 text-navy font-medium">{m.name}</td>
                  <td className="px-4 py-3 text-slate-600">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${ROLE_BADGE[m.role]}`}>{m.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{m.phone || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{m.member_category || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(m.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(m)} className="text-[11px] uppercase tracking-widest text-club-red hover:underline" data-testid={`edit-member-${m.id}`}>{tr("Éditer")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {editing && <EditModal member={editing} onClose={() => setEditing(null)} reload={load} userRole={user.role} t={t} />}
    </div>
  );
}

function EditModal({ member, onClose, reload, userRole, t }) {
  const [form, setForm] = useState({
    name: member.name || "", phone: member.phone || "", address: member.address || "",
    member_category: member.member_category || "", role: member.role || "member",
  });
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
      await api.patch(`/secretariat/members/${member.id}`, fd);
      setDone(true);
      reload();
      setTimeout(onClose, 1200);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto" data-testid="edit-member-modal">
      <form onSubmit={save} className="bg-white max-w-xl w-full p-8 relative my-12">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="edit-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">{tr("Éditer membre")}</div>
        <h2 className="font-serif text-2xl text-navy mb-1">{member.email}</h2>
        <div className="divider-gold mt-4 mb-6" />

        <div className="space-y-4">
          <Field label={tr("Nom")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
          <Field label={tr("Téléphone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />
          <Field label={tr("Adresse")} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="edit-address" />
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{tr("Catégorie")}</label>
            <select value={form.member_category} onChange={(e) => setForm({ ...form, member_category: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5" data-testid="edit-category">
              <option value="">—</option>
              <option value="membre_effectif">{tr("Membre effectif")}</option>
              <option value="membre_sympathisant">{tr("Membre sympathisant")}</option>
              <option value="membre_etudiant">{tr("Étudiant")}</option>
              <option value="membre_jeune">Jeune</option>
              <option value="pensionne_effectif">{tr("Pensionné effectif")}</option>
              <option value="pensionne_sympathisant">{tr("Pensionné sympathisant")}</option>
            </select>
          </div>
          {userRole === "admin" && (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{tr("Rôle")}</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5" data-testid="edit-role">
                <option value="member">{tr("Membre")}</option>
                <option value="secretary">{tr("Secrétaire")}</option>
                <option value="treasurer">{tr("Trésorier")}</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}
        </div>

        {err && <div className="text-sm text-red-600 mt-4" data-testid="edit-error">{err}</div>}
        {done && <div className="flex items-center gap-2 text-sm text-green-700 mt-4" data-testid="edit-saved"><Check className="w-4 h-4" />{tr("Sauvegardé")}</div>}

        <button type="submit" className="mt-6 w-full bg-navy text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red" data-testid="edit-submit">
          Sauvegarder
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2.5" data-testid={testid} />
    </div>
  );
}
